// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import * as Sentry from '@sentry/react';
import { PlanLimitToastListener } from '@/account/components/plan-limit-toast-listener';
import { CollapsibleDeploymentAlert } from '@/components/collapsible-deployment-alert';
import { ErrorPage } from '@/components/error-page';
import { GlobalNotFound } from '@/components/global-not-found';
import { RefreshRounded } from '@/constants/hardfin-standard-icons';
import { ToastProvider } from '@/contexts/toast-provider';
import { useAppVersion } from '@/hooks/use-app-version';
import { FIVE_MINUTES_MS } from '@/utilities/time-constants';
import { Alert, IconButton, Snackbar } from '@mui/material';
import { QueryClient } from '@tanstack/react-query';
import {
  Outlet,
  createRootRouteWithContext,
  useRouter,
} from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

interface RouteContext {
  queryClient: QueryClient;
}

const routeWithContext = createRootRouteWithContext<RouteContext>();

export const Route = routeWithContext({
  component: RootRoute,
  onError: (error) => Sentry.captureException(error),
  errorComponent: ErrorPage,
  notFoundComponent: GlobalNotFound,
});

function RootRoute() {
  const { isLatest, isApiCompatible, checkApiVersion } = useAppVersion();
  const router = useRouter();
  const hasScheduledReloadRef = useRef(false);
  const lastNavigationTimeRef = useRef(Date.now());

  // Check if user has navigated within the threshold (actively using the app)
  const isRecentlyActive = () =>
    Date.now() - lastNavigationTimeRef.current < FIVE_MINUTES_MS;

  // Track navigation events to update last activity time
  useEffect(() => {
    const unsubscribe = router.subscribe('onLoad', () => {
      lastNavigationTimeRef.current = Date.now();
    });

    return () => {
      unsubscribe();
    };
  }, [router]);

  // Only show deployment notice when API is compatible (yellow warning takes priority)
  const showApiWarning = !isApiCompatible;
  const showDeploymentNotice = isApiCompatible && !isLatest;

  const handleClickReload = () => {
    window.location.reload();
  };

  // Handle visibility change - check API version when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // If we already know API is incompatible and user is inactive, reload immediately
        if (!isApiCompatible && !isRecentlyActive()) {
          window.location.reload();
          return;
        }

        // Otherwise check for updates (for cases where mismatch happened while tab was hidden)
        void checkApiVersion()
          .then((hasChanged) => {
            if (hasChanged && !isRecentlyActive()) {
              window.location.reload();
            }
          })
          .catch(() => {
            // Ignore errors
          });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkApiVersion, isApiCompatible]);

  // Handle auto-reload when user hasn't navigated in 5+ minutes and API is incompatible
  useEffect(() => {
    if (isApiCompatible) {
      return;
    }

    // If user hasn't navigated in 5+ minutes, reload immediately
    if (!isRecentlyActive()) {
      window.location.reload();
      return;
    }

    // Set up an interval to check navigation age periodically
    const intervalId = setInterval(() => {
      if (!isRecentlyActive()) {
        window.location.reload();
      }
    }, 30000); // Check every 30 seconds

    return () => {
      clearInterval(intervalId);
    };
  }, [isApiCompatible]);

  // Handle reload on navigation when API is incompatible
  useEffect(() => {
    if (isApiCompatible || hasScheduledReloadRef.current) {
      return;
    }

    // Subscribe to route changes - reload on any navigation when API is incompatible
    const unsubscribe = router.subscribe('onBeforeNavigate', () => {
      if (!isApiCompatible) {
        hasScheduledReloadRef.current = true;
        window.location.reload();
      }
    });

    return () => {
      unsubscribe();
    };
  }, [isApiCompatible, router]);

  return (
    <>
      <ToastProvider>
        <PlanLimitToastListener />
        {/* Deployment version notice (blue info) - collapsible */}
        <CollapsibleDeploymentAlert
          open={showDeploymentNotice}
          onReload={handleClickReload}
        />

        {/* API version warning (yellow warning) - takes priority over deployment notice */}
        <Snackbar open={showApiWarning}>
          <Alert
            severity="warning"
            action={
              <IconButton size="small" onClick={handleClickReload}>
                <RefreshRounded fontSize="small" />
              </IconButton>
            }
          >
            You are using an outdated version of Hardfin. Reload to make sure
            everything works correctly.
          </Alert>
        </Snackbar>
        <Outlet />
      </ToastProvider>
    </>
  );
}
